import "leaflet/dist/leaflet.css";
import L from "leaflet";
import React, { useEffect, useRef, useState } from "react";
import { GeoJSON, LayersControl, MapContainer, TileLayer } from "react-leaflet";
import Bucket from "./Bucket.js";
import mapDataCOE from "/data/hcmCenters.json";
import mapDataState from "/data/states_simplified1.json";
import useCohortType from "./Stores/CohortStore.js";
import useAgeType from "./Stores/AgeStore.js";
import useSexType from "./Stores/SexStore.js";
import useInsuranceType from "./Stores/InsuranceStore.js";
import useYearType from "./Stores/YearStore.js";
import useStateType from "./Stores/StateStore.js";
import ReactGA from "react-ga4";
import { renderToStaticMarkup } from "react-dom/server";
import StatePopupContent from "./MapComponents/PopUpData";
import USAvgText from "./MapComponents/USAvgText.js";
import LoadingState from "./LoadingState";
interface MapProps {
  centersCheck: boolean;
  clear: boolean;
  rk: number;
  displayValue: number;
}

interface QuantileDatum {
  [key: number]: number;
}

interface ParametersForAPITypes {
  cohort: number;
  age: number;
  sex: number;
  insurance: number;
}
interface MapDataItem {
  age: number;
  cohort: number;
  display: number;
  insurance: number;
  n: number;
  sex: number;
  state: string;
  state_code: string;
  value: number;
  year: number;
  _id: string;
}

const Map: React.FC<MapProps> = ({
  displayValue,
  centersCheck,
  clear,
  rk,
}): JSX.Element => {
  const cohortStringList = [
    "HCM",
    "oHCM",
    "nHCM",
    "symptomatic HCM",
    "asymptomatic HCM",
  ];
  const ageStringList = [
    "of all ages",
    "of ages <18",
    "of ages 18-34",
    "of ages 35-44",
    "of ages 45-54",
    "of ages 55-64",
    "of ages ≥65",
  ];
  const insuranceStringList = [
    " all insurance types",
    " commercial insurance",
    " Medicaid",
    " Medicare",
  ];
  const sexStringList = ["females and males", "female", "male"];
  const yearStringList = [
    " 2016-2026",
    " 2016",
    " 2017",
    " 2018",
    " 2019",
    " 2020",
    " 2021",
    " 2022",
    " 2023",
    " 2024",
    " 2025",
    " 2026",
  ];
  function convertGenderString(genderString: string) {
    if (genderString === "female") {
      return "females";
    } else if (genderString === "male") {
      return "males";
    } else if (genderString === "females and males") {
      return "females and males";
    } else {
      return genderString;
    }
  }

  const cohortValue = useCohortType((s) => s?.cohortValue ?? 0);
  const setDisabledCohort = useCohortType((s) => s.setDisabledCohort);

  const ageValue = useAgeType((s) => s?.ageValue ?? 0);
  const setDisabledAge = useAgeType((s) => s.setDisabledAge);

  const sexValue = useSexType((s) => s?.sexValue ?? 0);
  const setDisabledSex = useSexType((s) => s.setDisabledSex);

  const insuranceValue = useInsuranceType((s) => s?.insuranceValue ?? 0);
  const setDisabledInsurance = useInsuranceType((s) => s.setDisabledInsurance);

  const stateValue = useStateType((s) => s?.stateValue ?? 0);
  const stateList = useStateType((s) => s?.stateList ?? []);
  const yearValue = useYearType((s) => s?.yearValue ?? 0);

  let yearString = yearStringList[yearValue];
  let cohortString = cohortStringList[cohortValue];
  let ageString = ageStringList[ageValue];
  let sexString = sexStringList[sexValue];
  let insuranceString = insuranceStringList[insuranceValue];
  const stateString = stateValue === "All" ? "the US" : stateValue;

  //data for the map
  const [mapData, setMapData] = useState<MapDataItem[] | undefined>();
  const [quantileData, setQuantileData] = useState<
    QuantileDatum[] | undefined
  >();
  const [decimalPoint, setDecimalPoint] = useState<number>(0);
  const [avgValue, setAvgValue] = useState<string>("");
  const [mapColor, setMapColor] = useState<string>("#2A424B");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //checking buckets for them to be displayed
  const [checked0, setChecked0] = useState<boolean>(true);
  const [checked1, setChecked1] = useState<boolean>(true);
  const [checked2, setChecked2] = useState<boolean>(true);
  const [checked3, setChecked3] = useState<boolean>(true);
  const [checked4, setChecked4] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    window.innerWidth < 640 ? setIsMobile(true) : setIsMobile(false);
  }, []);

  const [popUpString, setPopUpString] = useState<string>("");

  let parametersForAPI: ParametersForAPITypes = {
    cohort: -1,
    age: -1,
    sex: -1,
    insurance: -1,
  };

  //dynamically determining legend steps because they are different for each selection
  let legend0: string = "";
  let legend1: string = "";
  let legend2: string = "";
  let legend3: string = "";
  let legend4: string = "";

  //after changing the value the screen focuses on the map
  const rkComp = useRef(rk);
  if (rkComp.current === 0) {
    rkComp.current = 10000;
  }

  //calls api, focuses the screen on map, checks all buckets
  //triggered by changes to any of the filter&display menu options
  useEffect(() => {
    fetchDatafromAPI();
    // window.scrollTo(rkComp.current, rkComp.current);
    rkComp.current++;
    setChecked0(true);
    setChecked1(true);
    setChecked2(true);
    setChecked3(true);
    setChecked4(true);
    legend0 = "";
    legend1 = "";
    legend2 = "";
    legend3 = "";
    legend4 = "";
    // if (displayValue) {
    //   ReactGA.event("select_display_value", {
    //     display_name: displayValue,
    //   });
    // }
    if (cohortValue) {
      ReactGA.event("select_cohort_type", {
        cohort_name: cohortValue,
      });
    }
  }, [
    cohortValue,
    ageValue,
    sexValue,
    insuranceValue,
    yearValue,
    stateValue,
    displayValue,
  ]);

  function formatNumberWithCommas(number: number) {
    if (number > 1000) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else {
      return number;
    }
  }

  //calling api function
  const fetchDatafromAPI = async () => {
    //makes them empty so that the map is triggered to render again when data comes & so that there isn't a value from the previous render for us average
    setIsLoading(true);
    setMapData(undefined);
    setQuantileData(undefined);
    setAvgValue("0");
    //first checks if any of the values are -1 (disabled)
    try {
      const API_URL = import.meta.env.VITE_REACT_APP_API_URI;
      const disableDataResponse = await fetch(
        `${API_URL}/Disable?display=${displayValue}`,
      ).catch((error) => {
        console.log(error);
      });
      const disableDataJson = await disableDataResponse?.json();
      //if any of the values is -1, disables that menu & gives -1 for the next api call
      if (disableDataJson.cohort) {
        setDisabledCohort(true);
      } else {
        setDisabledCohort(false);
        parametersForAPI.cohort = cohortValue;
      }

      if (disableDataJson.age) {
        setDisabledAge(true);
      } else {
        setDisabledAge(false);
        parametersForAPI.age = ageValue;
      }

      if (disableDataJson.sex) {
        setDisabledSex(true);
      } else {
        setDisabledSex(false);
        parametersForAPI.sex = sexValue;
      }

      if (disableDataJson.insurance) {
        setDisabledInsurance(true);
      } else {
        setDisabledInsurance(false);
        parametersForAPI.insurance = insuranceValue;
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
    //filters & calls data based on the selections
    try {
      const API_URL = import.meta.env.VITE_REACT_APP_API_URI;
      const apiUrl = `${API_URL}/MapData?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&insurance=${parametersForAPI.insurance}&year=${yearValue}&state=${stateValue}&display=${displayValue}`;
      const mapDataResponse = await fetch(apiUrl);
      if (!mapDataResponse) {
        console.error("No response received from MapData API");
        return;
      }

      if (!mapDataResponse.ok) {
        console.error(
          `MapData API error: ${mapDataResponse.status} ${mapDataResponse.statusText}`,
        );
        const errorText = await mapDataResponse.text();
        console.error("Error response:", errorText);
        return;
      }

      const mapDataJson = await mapDataResponse.json();
      setMapData(mapDataJson.data);
      setQuantileData(mapDataJson.quantiles);
      setPopUpString(mapDataJson.display);
      setDecimalPoint(mapDataJson.decimal);
      setChecked0(true);
      setChecked1(true);
      setChecked2(true);
      setChecked3(true);
      setChecked4(true);
      setAvgValue(mapDataJson.usAvg[0].value.toFixed(1));
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  //if legendSteps exists, it creates the labels for the legends
  if (quantileData && quantileData.length > 0) {
    legend0 = `${formatNumberWithCommas(
      Number(Number(quantileData[0]).toFixed(decimalPoint)),
    )}-${formatNumberWithCommas(
      Number(Number(quantileData[1]).toFixed(decimalPoint)),
    )}`;

    legend1 = `${formatNumberWithCommas(
      Number(Number(quantileData[2]).toFixed(decimalPoint)),
    )}-${formatNumberWithCommas(
      Number(Number(quantileData[3]).toFixed(decimalPoint)),
    )}`;
    legend2 = `${formatNumberWithCommas(
      Number(Number(quantileData[4]).toFixed(decimalPoint)),
    )}-${formatNumberWithCommas(
      Number(Number(quantileData[5]).toFixed(decimalPoint)),
    )}`;
    legend3 = `${formatNumberWithCommas(
      Number(Number(quantileData[6]).toFixed(decimalPoint)),
    )}-${formatNumberWithCommas(
      Number(Number(quantileData[7]).toFixed(decimalPoint)),
    )}`;
    legend4 = `${formatNumberWithCommas(
      Number(Number(quantileData[8]).toFixed(decimalPoint)),
    )}-${formatNumberWithCommas(
      Number(Number(quantileData[9]).toFixed(decimalPoint)),
    )}`;
  }

  //style options for the hcm centers
  var centersMarker = {
    radius: isMobile ? 3 : 6,
    fillColor: "black",
    color: "black",
    weight: 1,
    opacity: 8,
    fillOpacity: 8,
  };

  //style options for the states without data
  const defaultStyle = {
    fillColor: "gray",
    color: "gray",
    fillOpacity: 0,
    weight: 1,
  };

  const findStateData = (stateName: string, stateID?: string) =>
    mapData?.find(
      (item) =>
        item.state === stateName ||
        (stateID !== undefined && item.state_code === stateID),
    );

  //function to determine popUp data
  //return "no data" if the value is na (converted na values to -1 in the api so filtering is by greater than 0)
  const getPopUpData = (stateName: string, stateID: string) => {
    let num = -1;
    let nValue = 0;
    const stateData = findStateData(stateName, stateID);

    if (stateData && stateData.value >= 0) {
      num = stateData.value;
      nValue = stateData.n;
    }

    return renderToStaticMarkup(
      <StatePopupContent
        stateName={stateName}
        displayValue={displayValue}
        num={num}
        nValue={nValue}
        decimalPoint={decimalPoint}
        popUpString={popUpString}
        yearString={yearString}
        cohortString={cohortString}
        ageString={ageString}
        sexString={sexString}
        insuranceString={insuranceString}
        convertGenderString={convertGenderString}
        formatNumberWithCommas={formatNumberWithCommas}
      />,
    );
  };

  //filters features based the min and max values of the layer
  const filterFeatures = (
    geoJsonFeature: any,
    minValue: number,
    maxValue: number,
  ) => {
    const stateData = findStateData(
      geoJsonFeature.properties.state,
      geoJsonFeature.properties.abbrev,
    );

    if (!stateData || stateData.value < 0) return false;

    return stateData.value >= minValue && stateData.value <= maxValue;
  };

  //determines color based on legendSteps and displayValue
  const setColorGrade = (layer: any, stateName: string, stateID: string) => {
    const stateData = findStateData(stateName, stateID);
    const opacity = 1;
    layer.options.color = "gray";
    layer.options.weight = 1;

    if (!stateData || stateData.value < 0) {
      layer.options.fillOpacity = 0;
      return;
    }

    const val = stateData.value;
    if (quantileData) {
      if (displayValue < 4) {
        //Epi
        setMapColor("#2A424B");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#C2C8CA";
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#8F9BA0";
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#5C6E75";
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#2A424B";
        }
      } else if (displayValue < 26) {
        // Clinical
        setMapColor("#0F808E");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#BBD7DB";
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#82BAC1";
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#489DA7";
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#0F808E";
        }
      } else if (displayValue < 38) {
        //Treatment
        setMapColor("#1D3D71");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#BFC7D4";
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#8999B3";
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#536B92";
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#1D3D71";
        }
      } else if (displayValue < 54) {
        //Outcomes
        setMapColor("#09795E");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#BAD6CF";
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#7FB7A9";
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#449883";
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#09795E";
        }
      } else if (displayValue < 76) {
        //Economic
        setMapColor("#54BC7D");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#CCE6D7";
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#A4D8B9";
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#7CCA9B";
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#54BC7D";
        }
      } else if (displayValue > 61) {
        //Misc
        setMapColor("#677288");
        if (val <= Number(quantileData[1])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#F5F5F5";
          //"#F5F5F5" "#C2C8CA" "#8F9BA0" "#5C6E75" "#2A424B"
        } else if (val <= Number(quantileData[3])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#D1D4D9";
          //"#F5F5F5" "#BBD7DB" "#82BAC1" "#489DA7" "#0F808E"
        } else if (val <= Number(quantileData[5])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#AEB3BE";
          //"#F5F5F5" "#BFC7D4" "#8999B3" "#536B92" "#1D3D71"
        } else if (val <= Number(quantileData[7])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#8A92A3";
          //"#F5F5F5" "#BAD6CF" "#7FB7A9" "#449883" "#09795E"
        } else if (val <= Number(quantileData[9])) {
          layer.options.fillOpacity = opacity;
          layer.options.fillColor = "#677288";
          //"#F5F5F5" "#CCE6D7" "#A4D8B9" "#7CCA9B" "#54BC7D"
        }
        //"#F5F5F5" "#D1D4D9" "#AEB3BE" "#8A92A3" "#677288"
      }
    }

    layer.options.color = "gray";
    layer.options.weight = 1;
  };

  //calls necessary function for each state
  const onEachState = (state: any, layer: any) => {
    const stateID = state.properties.abbrev;
    const stateName = state.properties.state;

    const popUp = () => {
      layer.bindPopup(getPopUpData(stateName, stateID)).openPopup();
    };
    layer.on({
      mouseover: popUp,
    });

    setColorGrade(layer, stateName, stateID);
  };

  // Keeps states without data interactive while the bucket layers handle color.
  const onEachBaseState = (state: any, layer: any) => {
    const stateID = state.properties.abbrev;
    const stateName = state.properties.state;
    const stateData = findStateData(stateName, stateID);

    // A missing record means this state was not part of the current request
    // (for example, another state was selected), not that its value is -1.
    if (!stateData) return;

    layer.on({
      mouseover: () => {
        layer.bindPopup(getPopUpData(stateName, stateID)).openPopup();
      },
    });
  };

  //calls necessary function for each hcm center
  const onEachCenter = (centerPoint: any, layer: any) => {
    const state = centerPoint.properties.state;
    const hcm_coe = centerPoint.properties.coe;
    const popUp = () => {
      layer.bindPopup("<b> " + hcm_coe + ", " + state + " </b>").openPopup();
    };
    layer.on({
      mouseover: popUp,
    });
  };

  const bucketCount = 5;
  const checkedStates = [checked0, checked1, checked2, checked3, checked4];
  const setCheckedStates = [
    setChecked0,
    setChecked1,
    setChecked2,
    setChecked3,
    setChecked4,
  ];
  const legends = [legend0, legend1, legend2, legend3, legend4];

  return (
    <>
      {isLoading ? (
        <LoadingState
          displayValue={displayValue}
          className="min-h-min lg:w-[95%] lg:mx-auto"
        />
      ) : mapData && quantileData ? (
        <div className="self-stretch overflow-hidden rounded-xl flex-1 items-start justify-start lg:w-[95%] lg:mx-auto box-border h-[98%] mb-3">
          <MapContainer
            center={[39, -98]}
            bounds={[
              [24, -125],
              [50, -66],
            ]}
            zoom={isMobile ? 2 : 4}
            minZoom={3}
            zoomControl={true}
            scrollWheelZoom={true}
            className="h-[70%] relative"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <GeoJSON
              style={defaultStyle}
              data={mapDataState.features}
              onEachFeature={onEachBaseState}
            />

            {!clear && (
              <>
                <LayersControl collapsed={false} position="bottomright">
                  {Array.from({ length: bucketCount }).map((_, index) => (
                    <Bucket
                      checked={checkedStates[index]}
                      setChecked={setCheckedStates[index]}
                      legend={legends[index]}
                      legendStepMin={Number(quantileData[index * 2])}
                      legendStepMax={Number(quantileData[index * 2 + 1])}
                      mapDataStateFeatures={mapDataState.features}
                      filterFeatures={filterFeatures}
                      onEachState={onEachState}
                    />
                  ))}
                </LayersControl>

                {centersCheck && (
                  <GeoJSON
                    data={mapDataCOE}
                    onEachFeature={onEachCenter}
                    pointToLayer={(feature, latlng) =>
                      L.circleMarker(latlng, centersMarker)
                    }
                  />
                )}
              </>
            )}
          </MapContainer>
          <br></br>
          {mapColor ? (
            <USAvgText
              mapColor={mapColor}
              displayValue={displayValue}
              sexString={sexString}
              ageString={ageString}
              insuranceString={insuranceString}
              cohortString={cohortString}
              stateString={stateString}
              yearString={yearString}
              popUpString={popUpString}
              avgValue={avgValue}
              decimalPoint={decimalPoint}
              convertGenderString={convertGenderString}
              formatNumberWithCommas={formatNumberWithCommas}
            />
          ) : null}
        </div>
      ) : (
        <div />
      )}
    </>
  );
};

export default Map;
